import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { handyBreakpoint } from '~Styles/breakpoints';

/**
 * Every colour used in this modal, named and stated once.
 *
 * The surrounding POS inherits a lot of ambient colour; nothing in here does.
 * Each rule below sets its own colour explicitly so the panel reads the same
 * whatever it happens to be rendered over.
 */
export const C = {
  /** Panel body. */
  surface: '#161b23',
  /** Inset blocks (method tiles, stamp cards, discount block). */
  surfaceInset: 'var(--colorBlueGray1200)',
  /** Inset blocks while a finger is down on them. */
  surfacePressed: 'var(--colorBlueGray1100)',
  /** 1px separators and inactive geometry. */
  hairline: 'var(--colorBlueGray1100)',
  /** Member name, headings, stat values. */
  textPrimary: 'var(--colorBlueGray50)',
  /** Labels, captions, secondary stats. */
  textSecondary: '#94A0B2',
  /** Eyebrows, timestamps, muted captions. */
  textMuted: '#6B7787',
  /** Pre-identification accent, before we know the member's tier. */
  accent: '#17C964',
  accentWash: 'rgba(23, 201, 100, 0.10)',
  accentEdge: 'rgba(23, 201, 100, 0.32)',
  /** The accent wash under a finger. */
  accentPressed: 'rgba(23, 201, 100, 0.22)',
  /** Text on any light tier colour. */
  textOnLight: 'var(--colorBlueGray1300)',
  /** Failures. */
  danger: '#FF6B6B',
  dangerWash: 'rgba(255, 107, 107, 0.10)',
  /** Fill and text for the destructive disconnect action. */
  dangerSolid: '#D93B41',
  dangerPressed: '#B92F35',
  textOnDanger: '#FFF2F2',
} as const;

/**
 * Every state transition in here. A quick tap is shorter than the transition
 * that follows it, so anything slower than this finishes after the finger has
 * already gone and the press reads as lag.
 */
export const TRANSITION = '100ms ease';

const riseIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

/** The contactless arcs sweep outward, the way the symbol is read. */
export const sweep = keyframes`
  0%, 100% { opacity: 0.15; }
  50%      { opacity: 1; }
`;

/** Header and footer stay put; only the middle scrolls. */
export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  width: 520px;
  max-width: 100%;
  max-height: min(88vh, 880px);
  background: ${C.surface};
  border: 1px solid ${C.hairline};
  border-radius: 18px;
  box-shadow: 0 32px 80px rgba(0, 0, 0, 0.55);
  animation: ${riseIn} 220ms cubic-bezier(0.16, 1, 0.3, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  ${handyBreakpoint} {
    width: 440px;
    border-radius: 14px;
  }
`;

export const Header = styled.div`
  flex: none;
  padding: 28px 28px 20px;

  ${handyBreakpoint} {
    padding: 20px 20px 14px;
  }
`;

export const Body = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Bottom padding matters on the steps that have no footer under them. */
  padding: 0 28px 28px;

  scrollbar-width: thin;
  scrollbar-color: ${C.hairline} transparent;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${C.hairline};
    border-radius: 4px;
  }

  ${handyBreakpoint} {
    padding: 0 20px 20px;
  }
`;

export const Footer = styled.div`
  flex: none;
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 10px;
  padding: 20px 28px 24px;
  border-top: 1px solid ${C.hairline};
  background: ${C.surface};
  border-radius: 0 0 18px 18px;

  ${handyBreakpoint} {
    padding: 14px 20px 18px;
    border-radius: 0 0 14px 14px;
  }
`;

export const Eyebrow = styled.div`
  color: ${C.textMuted};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;

  ${handyBreakpoint} {
    font-size: 10px;
  }
`;

export const Heading = styled.h2`
  margin: 6px 0 0;
  color: ${C.textPrimary};
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;

  ${handyBreakpoint} {
    font-size: 19px;
  }
`;

export const SectionLabel = styled.div`
  margin: 24px 0 12px;
  color: ${C.textMuted};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;

  ${handyBreakpoint} {
    margin: 18px 0 10px;
    font-size: 10px;
  }
`;

/* ---- waiting ---------------------------------------------------------- */

export const Waiting = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 48px 0;
  color: ${C.accent};

  ${handyBreakpoint} {
    gap: 14px;
    padding: 36px 0;
  }
`;

export const WaitingLabel = styled.div`
  color: ${C.textSecondary};
  font-size: 15px;

  ${handyBreakpoint} {
    font-size: 13px;
  }
`;

export const SpinnerRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

/* ---- member header ----------------------------------------------------- */

export const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;

  ${handyBreakpoint} {
    margin-top: 14px;
  }
`;

export const MemberName = styled.div`
  color: ${C.textPrimary};
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;

  ${handyBreakpoint} {
    font-size: 25px;
  }
`;

export const MemberMeta = styled.div`
  margin-top: 8px;
  color: ${C.textSecondary};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;

  ${handyBreakpoint} {
    font-size: 11px;
  }
`;

/* ---- stats ------------------------------------------------------------ */

export const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 11px 0;
  border-top: 1px solid ${C.hairline};

  ${handyBreakpoint} {
    padding: 9px 0;
  }
`;

export const StatLabel = styled.span`
  color: ${C.textSecondary};
  font-size: 13px;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

export const StatValue = styled.span`
  color: ${C.textPrimary};
  font-size: 13px;
  font-weight: 600;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

export const CardValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${C.textPrimary};
  font-size: 13px;
  font-weight: 600;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-variant-numeric: tabular-nums;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

/* ---- actions ---------------------------------------------------------- */

export const ActionButton = styled.button<{ touching?: boolean }>`
  height: 58px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  /* Sinks under the finger so the press is felt on a glass counter screen. */
  transform: ${({ touching }) => (touching ? 'scale(0.98)' : 'scale(1)')};
  transition: filter ${TRANSITION}, background ${TRANSITION}, border-color ${TRANSITION},
    color ${TRANSITION}, transform ${TRANSITION};

  &:focus-visible {
    outline: 2px solid ${C.accent};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.55;
    cursor: default;
    transform: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }

  ${handyBreakpoint} {
    height: 50px;
    font-size: 14px;
  }
`;

export const CancelButton = styled(ActionButton)`
  background: ${({ touching }) => (touching ? C.surfacePressed : 'transparent')};
  border: 1px solid ${({ touching }) => (touching ? C.textMuted : C.hairline)};
  color: ${({ touching }) => (touching ? C.textPrimary : C.textSecondary)};

  &:hover:not(:disabled) {
    color: ${C.textPrimary};
    border-color: ${C.textMuted};
  }
`;

export const ConfirmButton = styled(ActionButton)`
  background: ${C.accent};
  border: 1px solid ${C.accent};
  color: ${C.textOnLight};
  /* Darkens rather than brightens: on a light fill, pressed should read heavier. */
  filter: ${({ touching }) => (touching ? 'brightness(0.9)' : 'none')};

  &:hover:not(:disabled) {
    filter: ${({ touching }) => (touching ? 'brightness(0.9)' : 'brightness(1.06)')};
  }
`;

export const DisconnectButton = styled(ActionButton)`
  background: ${({ touching }) => (touching ? C.dangerPressed : C.dangerSolid)};
  border: 1px solid ${({ touching }) => (touching ? C.dangerPressed : C.dangerSolid)};
  color: ${C.textOnDanger};
`;

export const CloseButton = styled(ActionButton)`
  background: ${({ touching }) => (touching ? C.surfacePressed : C.surfaceInset)};
  border: 1px solid ${C.hairline};
  color: ${C.textPrimary};
`;

export const WarningNote = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${C.dangerWash};
  border: 1px solid rgba(255, 107, 107, 0.28);
  color: ${C.danger};
  font-size: 14px;
  font-weight: 600;

  ${handyBreakpoint} {
    padding: 11px 13px;
    font-size: 13px;
  }
`;

export const WarningSub = styled.span`
  display: block;
  margin-top: 3px;
  color: ${C.textSecondary};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;

  ${handyBreakpoint} {
    font-size: 11px;
  }
`;

export const SecondaryAction = styled.button<{ touching?: boolean }>`
  width: 100%;
  height: 52px;
  margin-top: 12px;
  cursor: pointer;
  border-radius: 12px;
  background: ${({ touching }) => (touching ? C.surfacePressed : 'transparent')};
  border: 1px dashed ${({ touching }) => (touching ? C.textMuted : C.hairline)};
  color: ${({ touching }) => (touching ? C.textPrimary : C.textSecondary)};
  font-size: 14px;
  font-weight: 600;
  transition: background ${TRANSITION}, border-color ${TRANSITION}, color ${TRANSITION};

  &:hover {
    color: ${C.textPrimary};
    border-color: ${C.textMuted};
  }

  &:focus-visible {
    outline: 2px solid ${C.accent};
    outline-offset: 2px;
  }

  ${handyBreakpoint} {
    height: 46px;
    font-size: 13px;
  }
`;

/* ---- fields ------------------------------------------------------------ */

export const EmptyState = styled.div`
  padding: 18px 0 4px;
  color: ${C.textMuted};
  font-size: 13px;
`;

/* ---- releasing --------------------------------------------------------- */

/**
 * Releasing a card is a round trip to the terminal and can take seconds, so it
 * gets words rather than a dimmed button.
 */
export const ReleasingBar = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 58px;
  border-radius: 12px;
  background: ${C.surfaceInset};
  border: 1px solid ${C.hairline};
  color: ${C.textSecondary};
  font-size: 15px;
  font-weight: 600;

  ${handyBreakpoint} {
    height: 50px;
    font-size: 13px;
  }
`;

export const ReleasingNote = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  color: ${C.textSecondary};
  font-size: 13px;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

export const ErrorNote = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: ${C.dangerWash};
  color: ${C.danger};
  font-size: 13px;
  line-height: 1.4;

  ${handyBreakpoint} {
    padding: 10px 12px;
    font-size: 12px;
  }
`;
